import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersAPI, partnersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Search, 
  MoreHorizontal,
  UserCog,
  Shield,
  Building2,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  UserCheck,
  ChevronDown,
  ArrowUpCircle,
  ArrowDownCircle,
  Power,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  superadmin: { label: 'Super Admin', color: 'bg-amber-100 text-amber-800', icon: Crown },
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  pm: { label: 'Project Manager', color: 'bg-blue-100 text-blue-800', icon: UserCheck },
};

const TECHNOFOCUS_DOMAINS = ['technofocus.in', 'technofocus.co'];

const getUserStatusDisplay = (user) => {
  if (user.status === 'pending') return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
  if (user.status === 'denied') return { label: 'Denied', color: 'bg-red-100 text-red-800' };
  if (!user.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-800' };
  return { label: 'Active', color: 'bg-green-100 text-green-800' };
};

export const UsersPage = () => {
  const { user: currentUser, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [activeTab, setActiveTab] = useState('all');

  const isTechnofocusDomain = (domain) => {
    return TECHNOFOCUS_DOMAINS.includes(domain?.toLowerCase());
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, partnersData] = await Promise.all([
        usersAPI.getAll(true), // include inactive
        partnersAPI.getAll(),
      ]);
      
      // Separate pending users for the approval tab, but show ALL in main tab
      const pending = usersData.filter(u => u.status === 'pending');
      
      setUsers(usersData); // Show ALL users in the All Users tab
      setPendingUsers(pending);
      setPartners(partnersData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if current user can manage target user
  const canManageUser = (targetUser) => {
    if (!currentUser || !targetUser) return false;
    
    // Cannot manage yourself
    if (currentUser.email === targetUser.email) return false;
    
    // Super admin can manage anyone
    if (isSuperAdmin && isSuperAdmin()) return true;
    
    // Admin cannot manage superadmins
    if (isAdmin() && targetUser.role === 'superadmin') return false;
    
    // Admin can manage others
    if (isAdmin()) return true;
    
    return false;
  };

  // Check if PM can be promoted to Admin (only by Super Admin)
  const canPromoteToAdmin = (targetUser) => {
    if (!isSuperAdmin || !isSuperAdmin()) return false;
    if (targetUser.role !== 'pm') return false;
    return true;
  };

  // Check if admin can be promoted to Super Admin (only Technofocus admins, by Super Admin)
  const canPromoteToSuperAdmin = (targetUser) => {
    if (!isSuperAdmin || !isSuperAdmin()) return false;
    if (targetUser.role !== 'admin') return false;
    return isTechnofocusDomain(targetUser.domain);
  };

  // Check if admin can be demoted to PM (only by Super Admin)
  const canDemoteToPM = (targetUser) => {
    if (!isSuperAdmin || !isSuperAdmin()) return false;
    if (targetUser.role !== 'admin') return false;
    return true;
  };

  // Check if user can be permanently removed (Super Admin only, not self, not protected)
  const canRemoveUser = (targetUser) => {
    if (!isSuperAdmin || !isSuperAdmin()) return false;
    if (currentUser?.email === targetUser.email) return false;
    const PROTECTED = ['admin@technofocus.in', 'ranjana.jain@technofocus.in'];
    if (PROTECTED.includes(targetUser.email)) return false;
    return true;
  };

  const handleApproveUser = async (userId) => {
    try {
      await usersAPI.approveUser(userId);
      toast.success('User approved successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleDenyUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deny this user?')) return;
    
    try {
      await usersAPI.denyUser(userId);
      toast.success('User denied');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny user');
    }
  };

  const handlePromoteToAdmin = async (userId) => {
    if (!window.confirm('Promote this user to Admin?')) return;
    
    try {
      await usersAPI.promoteToAdmin(userId);
      toast.success('User promoted to Admin');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleDemoteToPM = async (userId) => {
    if (!window.confirm('Demote this Admin to Project Manager?')) return;
    
    try {
      await usersAPI.demoteToPM(userId);
      toast.success('User demoted to Project Manager');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to demote user');
    }
  };

  const handlePromoteToSuperAdmin = async (userId) => {
    if (!window.confirm('Promote this admin to Super Admin? This grants full system access.')) return;
    
    try {
      await usersAPI.promoteToSuperAdmin(userId);
      toast.success('User promoted to Super Admin');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await usersAPI.toggleActive(userId);
      toast.success('User status updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (!window.confirm(`Permanently delete ${userEmail}? This action cannot be undone.`)) return;
    try {
      await usersAPI.permanentDelete(userId);
      toast.success('User permanently deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setSelectedPartners(user.assigned_partners || []);
    setAssignDialogOpen(true);
  };

  const handleAssignPartners = async () => {
    try {
      await usersAPI.assignPartners(selectedUser.id, selectedPartners);
      toast.success('Partners assigned successfully');
      setAssignDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign partners');
    }
  };

  const togglePartner = (partnerId) => {
    setSelectedPartners(prev => 
      prev.includes(partnerId)
        ? prev.filter(id => id !== partnerId)
        : [...prev, partnerId]
    );
  };

  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.domain?.toLowerCase().includes(searchLower)
    );
  });

  const getPartnerNames = (partnerIds) => {
    if (!partnerIds?.length) return 'None';
    return partnerIds
      .map(id => partners.find(p => p.id === id)?.code || id)
      .join(', ');
  };

  // Only Admins can access this page
  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only administrators can access user management.</p>
      </div>
    );
  }

  const renderUserRow = (user, showApprovalActions = false) => {
    const roleInfo = ROLE_LABELS[user.role] || ROLE_LABELS.pm;
    const RoleIcon = roleInfo.icon;
    const statusDisplay = getUserStatusDisplay(user);
    const canManage = canManageUser(user);
    const isCurrentUser = currentUser?.email === user.email;
    const isPending = user.status === 'pending';

    return (
      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
              <RoleIcon className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{user.name}</p>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs">You</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {user.email}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={isTechnofocusDomain(user.domain) ? 'border-amber-300 bg-amber-50' : ''}>
            {user.domain}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge className={roleInfo.color}>
            {roleInfo.label}
          </Badge>
        </TableCell>
        <TableCell>
          {user.role === 'pm' ? (
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{getPartnerNames(user.assigned_partners)}</span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">All partners</span>
          )}
        </TableCell>
        <TableCell>
          <Badge className={statusDisplay.color}>
            {statusDisplay.label}
          </Badge>
        </TableCell>
        <TableCell>
          {showApprovalActions ? (
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={() => handleApproveUser(user.id)}
                className="bg-green-600 hover:bg-green-700"
                data-testid={`approve-user-${user.id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={() => handleDenyUser(user.id)}
                data-testid={`deny-user-${user.id}`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Deny
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={!canManage}>
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* Approve/Deny for pending users */}
                {isPending && canManage && (
                  <>
                    <DropdownMenuItem onClick={() => handleApproveUser(user.id)} className="text-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDenyUser(user.id)} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}

                {/* Assign Partners - Only for PMs */}
                {user.role === 'pm' && canManage && !isPending && (
                  <DropdownMenuItem onClick={() => openAssignDialog(user)}>
                    <Building2 className="w-4 h-4 mr-2" />
                    Assign Partners
                  </DropdownMenuItem>
                )}
                
                {/* Promote PM to Admin - Only Super Admin */}
                {canPromoteToAdmin(user) && !isPending && (
                  <DropdownMenuItem onClick={() => handlePromoteToAdmin(user.id)}>
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                    Promote to Admin
                  </DropdownMenuItem>
                )}
                
                {/* Demote Admin to PM - Only Super Admin */}
                {canDemoteToPM(user) && !isPending && (
                  <DropdownMenuItem onClick={() => handleDemoteToPM(user.id)}>
                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                    Demote to Project Manager
                  </DropdownMenuItem>
                )}
                
                {/* Promote Admin to Super Admin - Only for Super Admins */}
                {canPromoteToSuperAdmin(user) && !isPending && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handlePromoteToSuperAdmin(user.id)}>
                      <Crown className="w-4 h-4 mr-2" />
                      Promote to Super Admin
                    </DropdownMenuItem>
                  </>
                )}
                
                {/* Toggle Active Status */}
                {canManage && !isPending && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleToggleActive(user.id)}
                      className={user.is_active ? 'text-orange-600' : 'text-green-600'}
                    >
                      <Power className="w-4 h-4 mr-2" />
                      {user.is_active ? 'Disable User' : 'Enable User'}
                    </DropdownMenuItem>
                  </>
                )}

                {/* Permanent Delete - Super Admin only */}
                {canRemoveUser(user) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleRemoveUser(user.id, user.email)}
                      className="text-destructive focus:text-destructive"
                      data-testid={`remove-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove User
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">User Management</h1>
          <p className="text-muted-foreground">
            {isAdmin() ? 'Manage user accounts, roles, and permissions' : 'Approve and manage user access'}
          </p>
        </div>
        {pendingUsers.length > 0 && (
          <Badge className="bg-yellow-100 text-yellow-800 text-sm py-1 px-3">
            <Clock className="w-4 h-4 mr-1" />
            {pendingUsers.length} Pending Approval{pendingUsers.length > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
        <TabsTrigger value="all">All Users ({users.length})</TabsTrigger>
          <TabsTrigger value="pending" className="relative">
            Pending Approval
            {pendingUsers.length > 0 && (
              <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full px-2 py-0.5">
                {pendingUsers.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name, email, or domain..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-users"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : filteredUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned Partners</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => renderUserRow(user))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <UserCog className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No users found</h3>
                  <p className="text-muted-foreground">
                    {search ? 'Try adjusting your search' : 'Users will appear here when they register'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map(i => (
                    <div key={i} className="h-16 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : pendingUsers.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Domain</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned Partners</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((user) => renderUserRow(user, true))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No pending approvals</h3>
                  <p className="text-muted-foreground">
                    All user requests have been processed
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Partners Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Partners</DialogTitle>
            <DialogDescription>
              Select which partners {selectedUser?.name} can manage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-64 overflow-y-auto">
            {partners.map(partner => (
              <div 
                key={partner.id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                onClick={() => togglePartner(partner.id)}
              >
                <Checkbox 
                  checked={selectedPartners.includes(partner.id)}
                  onCheckedChange={() => togglePartner(partner.id)}
                />
                <div className="flex-1">
                  <p className="font-medium">{partner.name}</p>
                  <p className="text-sm text-muted-foreground">{partner.code}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignPartners}
              className="bg-lime text-lime-foreground hover:bg-lime/90"
            >
              Save Assignments
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
